/**
 * Authentication Context
 * Provides auth state and methods throughout the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/auth';
import { fetchAuthSession } from '@aws-amplify/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check authentication state on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    setLoading(true);
    try {
      const result = await authService.getCurrentUser();
      if (result.success && result.user) {
        setUser(result.user);
        setIsAuthenticated(true);
        // Check for admin group in ID token
        try {
          const session = await fetchAuthSession();
          const groups = session.tokens?.idToken?.payload?.['cognito:groups'] || [];
          setIsAdmin(groups.includes('admin'));
        } catch {
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Auth state check error:', error);
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const login = useCallback(async (email, password) => {
    const result = await authService.signIn(email, password);
    if (result.success) {
      await checkAuthState();
    }
    return result;
  }, []);

  const logout = useCallback(async () => {
    const result = await authService.signOut();
    if (result.success) {
      setUser(null);
      setIsAuthenticated(false);
    }
    return result;
  }, []);

  const signup = useCallback(async (email, password) => {
    return authService.signUp(email, password);
  }, []);

  const confirmSignup = useCallback(async (email, code) => {
    return authService.confirmSignUp(email, code);
  }, []);

  const resendCode = useCallback(async (email) => {
    return authService.resendCode(email);
  }, []);

  const getAuthToken = useCallback(async () => {
    return authService.getAuthToken();
  }, []);

  const getUserEmail = useCallback(() => {
    if (user) {
      return user.signInDetails?.loginId || user.username || null;
    }
    return null;
  }, [user]);

  const value = {
    user,
    loading,
    isAuthenticated,
    isAdmin,
    login,
    logout,
    signup,
    confirmSignup,
    resendCode,
    getAuthToken,
    getUserEmail,
    refreshAuth: checkAuthState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
